/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
/**
 * 接口模块芯片类型，用于高端首页3D topo图展示
 */
export enum IntfChip {
    FE = 'FE',
    BE = 'BE',
    SO = 'SO',
    MGMT = 'Management',
}

/**
 * 接口模块端口类型，用于设备图页面表格展示
 */
export enum IntfPort {
    FC = 'fcPort',
    AI = 'AI',
    ETH = 'ethPort',
    SAS = 'sasPort',
    RoCE = 'rocePort',
    MGMT = 'managePort'
}

/**
 * 接口模块图像类型，用于设备图绘图
 */
export enum IntfGraph {
    /**
     * 4端口 FE 1Gb ETH I/O模块
     */
    ETH = 'ETH',
    /**
     * 4端口 BE 12Gb SAS I/O模块
     */
    SAS = 'SAS',
    /**
     * AI加速卡
     */
    AI = 'AI',
    /**
     * 4端口 (FE/SO) (8/16/32)Gb (FC/ETH) I/O模块
     */
    SMART = 'SMART',
    /**
     * 2端口 (FE/BE/SO) (40/100)Gb (ETH/RDMA/ROCE) I/O模块
     */
    IP40100 = 'IP40100',
    /**
     * 管理模块
     */
    SMM = 'SMM',
    /**
     * 方形管理模块（北辰8U等机框使用）
     */
    SMM_SQUARE = 'SMM_SQUARE',

    /**
     * 2端口 FE 1GB ETH I/O模块
     */
    ETH_V2 = 'ETH_V2',

    /**
     * 2端口 (FE/BE/SO) (40/100)Gb (ETH/RDMA/ROCE) I/O模块
     */
    IP40100_V2 = 'IP40100_V2',

    /**
     * 5910左标卡
     */
    DB5910_left = 'DB5910_left',

    /**
     * 5910右标卡
     */
    DB5910_right = 'DB5910_right',

    /**
     * OceanCyber 勒索侦测卡
     */
    OCEAN_CYBER_C_300 = 'OCEAN_CYBER_C_300',

    /**
     * HyperDetect 勒索软件检测模块
     */
    HYPER_DETECT = 'HYPER_DETECT',
}

/**
 * 接口模块名称
 */
export enum IntfI8nKey {
    ETH_4X1 = 'system.EnumDefine_INTF_MODEL_E_ETH_4X1',
    SAS_4X12 = 'system.EnumDefine_INTF_MODEL_E_SAS_4X12',
    AI = 'system.EnumDefine_INTF_MODEL_E_AI',
    SAS_V2_4X12 = 'system.EnumDefine_INTF_MODEL_E_SAS_V2_4X12',
    FC_4X8 = 'system.EnumDefine_INTF_MODEL_E_FC_4X8',
    FC_4X16 = 'system.EnumDefine_INTF_MODEL_E_FC_4X16',
    FC_4X32 = 'system.EnumDefine_INTF_MODEL_E_FC_4X32',
    FC_4X64 = 'system.EnumDefine_INTF_MODEL_E_FC_4X64',
    ETH_4X10 = 'system.EnumDefine_INTF_MODEL_E_ETH_4X10',
    ETH_4X25 = 'system.EnumDefine_INTF_MODEL_E_ETH_4X25',
    RDMA_4X25 = 'system.EnumDefine_INTF_MODEL_E_RDMA_4X25',
    ETH_2X40 = 'system.EnumDefine_INTF_MODEL_E_ETH_2X40',
    ETH_2X100 = 'system.EnumDefine_INTF_MODEL_E_ETH_2X100',
    ETH_2X200 = 'system.EnumDefine_INTF_MODEL_E_ETH_2X200',
    RDMA_BE_2X100 = 'system.EnumDefine_INTF_MODEL_E_RDMA_BE_2X100',
    RDMA_SO_2X100 = 'system.EnumDefine_INTF_MODEL_E_RDMA_SO_2X100',
    RDMA_SO_2X200 = 'system.EnumDefine_INTF_MODEL_E_RDMA_2X200',
    ROCE_4X10 = 'system.EnumDefine_INTF_MODEL_E_ROCE_4X10',
    ROCE_4X25 = 'system.EnumDefine_INTF_MODEL_E_ROCE_4X25',
    ROCE_2X40 = 'system.EnumDefine_INTF_MODEL_E_ROCE_2X40',
    ROCE_2X100 = 'system.EnumDefine_INTF_MODEL_E_ROCE_2X100',
    ROCE_2X200 = 'system.EnumDefine_INTF_MODEL_E_ROCE_2X200',
    MGMT = 'system.EnumDefine_INTF_MODEL_E_ManagerBoard',
    ETH_2X1 = 'system.EnumDefine_INTF_MODEL_E_FE_2X1',
    FA_ROCE_2X100 = 'system.EnumDefine_INTF_MODEL_E_FA_2X100',
    FA_ROCE_4X25 = 'system.EnumDefine_INTF_MODEL_E_FA_4X25',
    FE_NOF_4X25 = 'system.EnumDefine_INTF_MODEL_E_NOF_4X25',
    FE_NOF_2X100 = 'system.EnumDefine_INTF_MODEL_E_NOF_2X100',
    FE_NOF_2X200 = 'system.EnumDefine_INTF_MODEL_E_NOF_2X200',
    ROCE_2X25 = 'system.EnumDefine_INTF_MODEL_E_FE_2X25',
    OCEAN_CYBER_C_300 = 'system.EnumDefine_INTF_MODEL_E_OceanCyber_C300',
    HYPER_DETECT = 'system.EnumDefine_INTF_MODEL_E_HyperDetect',
}

export interface IntfModule {
    /**
     * 芯片类型
     */
    chip: IntfChip;
    /**
     * 端口类型
     */
    port: IntfPort;
    /**
     * 图片类型
     */
    graph: IntfGraph;
    /**
     * 名称的i18n key
     */
    i18nKey: IntfI8nKey;
}

/**
 * 生成接口卡模型
 * @param chip 芯片类型
 * @param port 端口类型
 * @param graph 图片类型
 * @param i18nKey 名称的i18n key
 */
const get = (chip: IntfChip, port: IntfPort, graph: IntfGraph, i18nKey: IntfI8nKey): IntfModule => ({
    chip,
    port,
    graph,
    i18nKey,
});

export const intfModuleList: { [key: string]: IntfModule } = {
    516: get(IntfChip.FE, IntfPort.ETH, IntfGraph.ETH, IntfI8nKey.ETH_4X1),
    518: get(IntfChip.BE, IntfPort.SAS, IntfGraph.SAS, IntfI8nKey.SAS_4X12),
    529: get(IntfChip.BE, IntfPort.AI, IntfGraph.AI, IntfI8nKey.AI),
    535: get(IntfChip.BE, IntfPort.AI, IntfGraph.AI, IntfI8nKey.AI),
    537: get(IntfChip.FE, IntfPort.ETH, IntfGraph.ETH, IntfI8nKey.ETH_4X1),
    538: get(IntfChip.BE, IntfPort.SAS, IntfGraph.SAS, IntfI8nKey.SAS_4X12),
    580: get(IntfChip.FE, IntfPort.ETH, IntfGraph.ETH, IntfI8nKey.ETH_4X1),
    583: get(IntfChip.BE, IntfPort.SAS, IntfGraph.SAS, IntfI8nKey.SAS_V2_4X12),
    601: get(IntfChip.FE, IntfPort.ETH, IntfGraph.ETH, IntfI8nKey.ETH_4X1),
    2304: get(IntfChip.FE, IntfPort.FC, IntfGraph.SMART, IntfI8nKey.FC_4X8),
    2305: get(IntfChip.FE, IntfPort.FC, IntfGraph.SMART, IntfI8nKey.FC_4X16),
    2306: get(IntfChip.FE, IntfPort.FC, IntfGraph.SMART, IntfI8nKey.FC_4X32),
    2307: get(IntfChip.FE, IntfPort.ETH, IntfGraph.SMART, IntfI8nKey.ETH_4X10),
    2308: get(IntfChip.FE, IntfPort.ETH, IntfGraph.SMART, IntfI8nKey.ETH_4X25),
    2309: get(IntfChip.SO, IntfPort.ETH, IntfGraph.SMART, IntfI8nKey.RDMA_4X25),
    2310: get(IntfChip.FE, IntfPort.FC, IntfGraph.SMART, IntfI8nKey.FC_4X8),
    2311: get(IntfChip.FE, IntfPort.FC, IntfGraph.SMART, IntfI8nKey.FC_4X16),
    2312: get(IntfChip.FE, IntfPort.FC, IntfGraph.SMART, IntfI8nKey.FC_4X32),
    2313: get(IntfChip.FE, IntfPort.ETH, IntfGraph.SMART, IntfI8nKey.ETH_4X10),
    2314: get(IntfChip.FE, IntfPort.ETH, IntfGraph.SMART, IntfI8nKey.ETH_4X25),
    2315: get(IntfChip.FE, IntfPort.ETH, IntfGraph.IP40100, IntfI8nKey.ETH_2X40),
    2316: get(IntfChip.FE, IntfPort.ETH, IntfGraph.IP40100, IntfI8nKey.ETH_2X100),
    2317: get(IntfChip.BE, IntfPort.ETH, IntfGraph.IP40100, IntfI8nKey.RDMA_BE_2X100),
    2318: get(IntfChip.SO, IntfPort.ETH, IntfGraph.IP40100, IntfI8nKey.RDMA_SO_2X100),
    2319: get(IntfChip.FE, IntfPort.ETH, IntfGraph.IP40100, IntfI8nKey.ETH_2X40),
    2320: get(IntfChip.FE, IntfPort.ETH, IntfGraph.IP40100, IntfI8nKey.ETH_2X100),
    2321: get(IntfChip.BE, IntfPort.ETH, IntfGraph.IP40100, IntfI8nKey.RDMA_BE_2X100),
    2322: get(IntfChip.SO, IntfPort.ETH, IntfGraph.IP40100, IntfI8nKey.RDMA_SO_2X100),
    2323: get(IntfChip.FE, IntfPort.RoCE, IntfGraph.SMART, IntfI8nKey.ROCE_4X10),
    2324: get(IntfChip.FE, IntfPort.RoCE, IntfGraph.SMART, IntfI8nKey.ROCE_4X25),
    2325: get(IntfChip.FE, IntfPort.RoCE, IntfGraph.SMART, IntfI8nKey.ROCE_4X10),
    2326: get(IntfChip.FE, IntfPort.RoCE, IntfGraph.SMART, IntfI8nKey.ROCE_4X25),
    2327: get(IntfChip.FE, IntfPort.RoCE, IntfGraph.IP40100, IntfI8nKey.ROCE_2X40),
    2328: get(IntfChip.FE, IntfPort.RoCE, IntfGraph.IP40100, IntfI8nKey.ROCE_2X100),
    2329: get(IntfChip.FE, IntfPort.RoCE, IntfGraph.IP40100, IntfI8nKey.ROCE_2X40),
    2330: get(IntfChip.FE, IntfPort.RoCE, IntfGraph.IP40100, IntfI8nKey.ROCE_2X100),
    2331: get(IntfChip.FE, IntfPort.ETH, IntfGraph.ETH, IntfI8nKey.ETH_4X10),   // 10Gb 电口卡
    2332: get(IntfChip.FE, IntfPort.ETH, IntfGraph.ETH, IntfI8nKey.ETH_4X10),   // 10Gb 电口卡
    2333: get(IntfChip.FE, IntfPort.FC, IntfGraph.SMART, IntfI8nKey.FC_4X8),
    2334: get(IntfChip.FE, IntfPort.FC, IntfGraph.SMART, IntfI8nKey.FC_4X16),
    2335: get(IntfChip.FE, IntfPort.FC, IntfGraph.SMART, IntfI8nKey.FC_4X32),
    2336: get(IntfChip.FE, IntfPort.ETH, IntfGraph.SMART, IntfI8nKey.ETH_4X10),
    2337: get(IntfChip.FE, IntfPort.ETH, IntfGraph.SMART, IntfI8nKey.ETH_4X25),
    2338: get(IntfChip.SO, IntfPort.ETH, IntfGraph.SMART, IntfI8nKey.RDMA_4X25),
    2339: get(IntfChip.FE, IntfPort.RoCE, IntfGraph.SMART, IntfI8nKey.ROCE_4X10),
    2340: get(IntfChip.FE, IntfPort.RoCE, IntfGraph.SMART, IntfI8nKey.ROCE_4X25),
    2341: get(IntfChip.FE, IntfPort.FC, IntfGraph.SMART, IntfI8nKey.FC_4X8),
    2342: get(IntfChip.FE, IntfPort.FC, IntfGraph.SMART, IntfI8nKey.FC_4X16),
    2343: get(IntfChip.FE, IntfPort.FC, IntfGraph.SMART, IntfI8nKey.FC_4X32),
    2344: get(IntfChip.FE, IntfPort.ETH, IntfGraph.SMART, IntfI8nKey.ETH_4X10),
    2345: get(IntfChip.FE, IntfPort.ETH, IntfGraph.SMART, IntfI8nKey.ETH_4X25),
    2346: get(IntfChip.FE, IntfPort.RoCE, IntfGraph.SMART, IntfI8nKey.ROCE_4X10),
    2347: get(IntfChip.FE, IntfPort.RoCE, IntfGraph.SMART, IntfI8nKey.ROCE_4X25),
    2348: get(IntfChip.FE, IntfPort.ETH, IntfGraph.IP40100, IntfI8nKey.ETH_2X40),
    2349: get(IntfChip.FE, IntfPort.ETH, IntfGraph.IP40100, IntfI8nKey.ETH_2X100),
    2350: get(IntfChip.BE, IntfPort.ETH, IntfGraph.IP40100, IntfI8nKey.RDMA_BE_2X100),
    2351: get(IntfChip.SO, IntfPort.ETH, IntfGraph.IP40100, IntfI8nKey.RDMA_SO_2X100),
    2352: get(IntfChip.FE, IntfPort.RoCE, IntfGraph.IP40100, IntfI8nKey.ROCE_2X40),
    2353: get(IntfChip.FE, IntfPort.RoCE, IntfGraph.IP40100, IntfI8nKey.ROCE_2X100),
    2354: get(IntfChip.FE, IntfPort.ETH, IntfGraph.IP40100, IntfI8nKey.ETH_2X40),
    2355: get(IntfChip.FE, IntfPort.ETH, IntfGraph.IP40100, IntfI8nKey.ETH_2X100),
    2356: get(IntfChip.BE, IntfPort.ETH, IntfGraph.IP40100, IntfI8nKey.RDMA_BE_2X100),
    2357: get(IntfChip.SO, IntfPort.ETH, IntfGraph.IP40100, IntfI8nKey.RDMA_SO_2X100),
    2358: get(IntfChip.FE, IntfPort.RoCE, IntfGraph.IP40100, IntfI8nKey.ROCE_2X40),
    2359: get(IntfChip.FE, IntfPort.RoCE, IntfGraph.IP40100, IntfI8nKey.ROCE_2X100),
    2360: get(IntfChip.FE, IntfPort.ETH, IntfGraph.ETH, IntfI8nKey.ETH_4X10),   // 10Gb 电口卡
    2361: get(IntfChip.SO, IntfPort.ETH, IntfGraph.SMART, IntfI8nKey.RDMA_4X25),
    2362: get(IntfChip.SO, IntfPort.ETH, IntfGraph.IP40100, IntfI8nKey.RDMA_SO_2X100),
    2363: get(IntfChip.SO, IntfPort.ETH, IntfGraph.IP40100, IntfI8nKey.RDMA_SO_2X100),
    2364: get(IntfChip.FE, IntfPort.ETH, IntfGraph.SMART, IntfI8nKey.ETH_4X10),
    2365: get(IntfChip.FE, IntfPort.ETH, IntfGraph.SMART, IntfI8nKey.ETH_4X25),
    2366: get(IntfChip.FE, IntfPort.ETH, IntfGraph.ETH, IntfI8nKey.ETH_4X10),   // 10Gb 电口卡
    2367: get(IntfChip.FE, IntfPort.RoCE, IntfGraph.SMART, IntfI8nKey.ROCE_4X25),
    2368: get(IntfChip.FE, IntfPort.FC, IntfGraph.SMART, IntfI8nKey.FC_4X8),
    2369: get(IntfChip.FE, IntfPort.FC, IntfGraph.SMART, IntfI8nKey.FC_4X16),
    2370: get(IntfChip.FE, IntfPort.FC, IntfGraph.SMART, IntfI8nKey.FC_4X32),
    2371: get(IntfChip.FE, IntfPort.ETH, IntfGraph.SMART, IntfI8nKey.ETH_4X10),
    2372: get(IntfChip.FE, IntfPort.ETH, IntfGraph.SMART, IntfI8nKey.ETH_4X25),
    2373: get(IntfChip.FE, IntfPort.RoCE, IntfGraph.SMART, IntfI8nKey.ROCE_4X25),

    2375: get(IntfChip.FE, IntfPort.FC, IntfGraph.SMART, IntfI8nKey.FC_4X8),
    2376: get(IntfChip.FE, IntfPort.FC, IntfGraph.SMART, IntfI8nKey.FC_4X16),
    2377: get(IntfChip.FE, IntfPort.FC, IntfGraph.SMART, IntfI8nKey.FC_4X32),
    2378: get(IntfChip.FE, IntfPort.ETH, IntfGraph.SMART, IntfI8nKey.ETH_4X10),
    2379: get(IntfChip.FE, IntfPort.ETH, IntfGraph.SMART, IntfI8nKey.ETH_4X25),
    2380: get(IntfChip.FE, IntfPort.RoCE, IntfGraph.SMART, IntfI8nKey.ROCE_4X10),

    2381: get(IntfChip.FE, IntfPort.RoCE, IntfGraph.SMART, IntfI8nKey.FE_NOF_4X25),

    2382: get(IntfChip.FE, IntfPort.ETH, IntfGraph.IP40100_V2, IntfI8nKey.ETH_2X40),
    2383: get(IntfChip.FE, IntfPort.ETH, IntfGraph.IP40100_V2, IntfI8nKey.ETH_2X100),
    2384: get(IntfChip.BE, IntfPort.ETH, IntfGraph.IP40100_V2, IntfI8nKey.RDMA_BE_2X100),
    2385: get(IntfChip.SO, IntfPort.ETH, IntfGraph.IP40100_V2, IntfI8nKey.RDMA_SO_2X100),
    2386: get(IntfChip.FE, IntfPort.RoCE, IntfGraph.IP40100_V2, IntfI8nKey.ROCE_2X40),
    2387: get(IntfChip.FE, IntfPort.RoCE, IntfGraph.IP40100_V2, IntfI8nKey.ROCE_2X100),

    2388: get(IntfChip.FE, IntfPort.RoCE, IntfGraph.IP40100_V2, IntfI8nKey.FE_NOF_2X100),

    2389: get(IntfChip.BE, IntfPort.SAS, IntfGraph.SAS, IntfI8nKey.SAS_4X12),
    2390: get(IntfChip.FE, IntfPort.ETH, IntfGraph.ETH, IntfI8nKey.ETH_4X1),

    2391: get(IntfChip.FE, IntfPort.ETH, IntfGraph.ETH_V2, IntfI8nKey.ETH_2X1),

    2392: get(IntfChip.SO, IntfPort.ETH, IntfGraph.SMART, IntfI8nKey.RDMA_4X25),
    2393: get(IntfChip.FE, IntfPort.RoCE, IntfGraph.SMART, IntfI8nKey.ROCE_4X25),
    2394: get(IntfChip.FE, IntfPort.FC, IntfGraph.SMART, IntfI8nKey.FC_4X8),
    2395: get(IntfChip.FE, IntfPort.FC, IntfGraph.SMART, IntfI8nKey.FC_4X16),
    2396: get(IntfChip.FE, IntfPort.FC, IntfGraph.SMART, IntfI8nKey.FC_4X32),
    2397: get(IntfChip.FE, IntfPort.ETH, IntfGraph.SMART, IntfI8nKey.ETH_4X10),
    2398: get(IntfChip.FE, IntfPort.ETH, IntfGraph.SMART, IntfI8nKey.ETH_4X25),
    2399: get(IntfChip.SO, IntfPort.ETH, IntfGraph.SMART, IntfI8nKey.RDMA_4X25),
    2400: get(IntfChip.FE, IntfPort.RoCE, IntfGraph.SMART, IntfI8nKey.ROCE_4X10),
    2401: get(IntfChip.FE, IntfPort.RoCE, IntfGraph.SMART, IntfI8nKey.ROCE_4X25),
    2402: get(IntfChip.FE, IntfPort.ETH, IntfGraph.IP40100_V2, IntfI8nKey.ETH_2X40),
    2403: get(IntfChip.FE, IntfPort.ETH, IntfGraph.IP40100_V2, IntfI8nKey.ETH_2X100),
    2404: get(IntfChip.BE, IntfPort.ETH, IntfGraph.IP40100_V2, IntfI8nKey.RDMA_BE_2X100),
    2405: get(IntfChip.SO, IntfPort.ETH, IntfGraph.IP40100_V2, IntfI8nKey.RDMA_SO_2X100),
    2406: get(IntfChip.FE, IntfPort.RoCE, IntfGraph.IP40100_V2, IntfI8nKey.ROCE_2X40),
    2407: get(IntfChip.FE, IntfPort.RoCE, IntfGraph.IP40100_V2, IntfI8nKey.ROCE_2X100),

    2408: get(IntfChip.FE, IntfPort.ETH, IntfGraph.SMART, IntfI8nKey.ETH_4X10),
    2409: get(IntfChip.FE, IntfPort.ETH, IntfGraph.SMART, IntfI8nKey.ETH_4X25),
    2410: get(IntfChip.FE, IntfPort.RoCE, IntfGraph.SMART, IntfI8nKey.ROCE_4X25),
    2411: get(IntfChip.FE, IntfPort.ETH, IntfGraph.ETH, IntfI8nKey.ETH_4X10), // 10Gb 电口卡
    2412: get(IntfChip.FE, IntfPort.ETH, IntfGraph.SMART, IntfI8nKey.ETH_4X10),
    2413: get(IntfChip.FE, IntfPort.ETH, IntfGraph.SMART, IntfI8nKey.ETH_4X25),
    2414: get(IntfChip.FE, IntfPort.RoCE, IntfGraph.SMART, IntfI8nKey.ROCE_4X25),
    2415: get(IntfChip.FE, IntfPort.ETH, IntfGraph.ETH, IntfI8nKey.ETH_4X10), // 10Gb 电口卡
    2416: get(IntfChip.FE, IntfPort.ETH, IntfGraph.ETH, IntfI8nKey.ETH_4X10), // 10Gb 电口卡
    // HY GE卡
    2417: get(IntfChip.FE, IntfPort.ETH, IntfGraph.ETH, IntfI8nKey.ETH_4X1),
    2418: get(IntfChip.FE, IntfPort.ETH, IntfGraph.DB5910_left, IntfI8nKey.ROCE_2X25),

    2419: get(IntfChip.FE, IntfPort.ETH, IntfGraph.SMART, IntfI8nKey.ETH_4X10),
    2420: get(IntfChip.FE, IntfPort.ETH, IntfGraph.SMART, IntfI8nKey.ETH_4X25),
    2421: get(IntfChip.FE, IntfPort.ETH, IntfGraph.SMART, IntfI8nKey.ETH_4X10),
    2422: get(IntfChip.FE, IntfPort.ETH, IntfGraph.SMART, IntfI8nKey.ETH_4X25),
    2437: get(IntfChip.FE, IntfPort.FC, IntfGraph.SMART, IntfI8nKey.FC_4X64),
    2438: get(IntfChip.FE, IntfPort.FC, IntfGraph.SMART, IntfI8nKey.FC_4X64),

    // 安全卡
    2423: get(IntfChip.BE, IntfPort.ETH, IntfGraph.OCEAN_CYBER_C_300, IntfI8nKey.OCEAN_CYBER_C_300),
    2424: get(IntfChip.BE, IntfPort.ETH, IntfGraph.OCEAN_CYBER_C_300, IntfI8nKey.OCEAN_CYBER_C_300),
    2425: get(IntfChip.BE, IntfPort.ETH, IntfGraph.HYPER_DETECT, IntfI8nKey.HYPER_DETECT),
    2426: get(IntfChip.BE, IntfPort.ETH, IntfGraph.HYPER_DETECT, IntfI8nKey.HYPER_DETECT),

    // VxLAN DTOE接口卡
    2427: get(IntfChip.FE, IntfPort.ETH, IntfGraph.SMART, IntfI8nKey.ETH_4X10),
    2428: get(IntfChip.FE, IntfPort.ETH, IntfGraph.SMART, IntfI8nKey.ETH_4X25),
    2429: get(IntfChip.FE, IntfPort.ETH, IntfGraph.IP40100_V2, IntfI8nKey.ETH_2X40),
    2430: get(IntfChip.FE, IntfPort.ETH, IntfGraph.IP40100_V2, IntfI8nKey.ETH_2X100),
    2431: get(IntfChip.FE, IntfPort.ETH, IntfGraph.IP40100_V2, IntfI8nKey.ETH_2X200),
    2432: get(IntfChip.FE, IntfPort.ETH, IntfGraph.SMART, IntfI8nKey.ETH_4X10),
    2433: get(IntfChip.FE, IntfPort.ETH, IntfGraph.SMART, IntfI8nKey.ETH_4X25),
    2434: get(IntfChip.FE, IntfPort.ETH, IntfGraph.IP40100_V2, IntfI8nKey.ETH_2X40),
    2435: get(IntfChip.FE, IntfPort.ETH, IntfGraph.IP40100_V2, IntfI8nKey.ETH_2X100),
    2436: get(IntfChip.FE, IntfPort.ETH, IntfGraph.IP40100_V2, IntfI8nKey.ETH_2X200),

    // 200G接口卡
    2439: get(IntfChip.FE, IntfPort.ETH, IntfGraph.IP40100_V2, IntfI8nKey.ETH_2X200),
    2440: get(IntfChip.FE, IntfPort.ETH, IntfGraph.IP40100_V2, IntfI8nKey.ETH_2X200),
    2441: get(IntfChip.FE, IntfPort.RoCE, IntfGraph.IP40100_V2, IntfI8nKey.ROCE_2X200),
    2442: get(IntfChip.FE, IntfPort.RoCE, IntfGraph.IP40100_V2, IntfI8nKey.ROCE_2X200),
    2443: get(IntfChip.FE, IntfPort.RoCE, IntfGraph.IP40100_V2, IntfI8nKey.FE_NOF_2X200),
    2444: get(IntfChip.SO, IntfPort.ETH, IntfGraph.IP40100_V2, IntfI8nKey.RDMA_SO_2X200),
    2445: get(IntfChip.SO, IntfPort.ETH, IntfGraph.IP40100_V2, IntfI8nKey.RDMA_SO_2X200),

    2446: get(IntfChip.FE, IntfPort.ETH, IntfGraph.IP40100_V2, IntfI8nKey.ETH_2X40),
    2447: get(IntfChip.FE, IntfPort.ETH, IntfGraph.IP40100_V2, IntfI8nKey.ETH_2X40),
    2448: get(IntfChip.FE, IntfPort.RoCE, IntfGraph.IP40100_V2, IntfI8nKey.ROCE_2X40),
    2449: get(IntfChip.FE, IntfPort.RoCE, IntfGraph.IP40100_V2, IntfI8nKey.ROCE_2X40),
    2450: get(IntfChip.FE, IntfPort.ETH, IntfGraph.IP40100_V2, IntfI8nKey.ETH_2X100),
    2451: get(IntfChip.FE, IntfPort.ETH, IntfGraph.IP40100_V2, IntfI8nKey.ETH_2X100),
    2452: get(IntfChip.FE, IntfPort.RoCE, IntfGraph.IP40100_V2, IntfI8nKey.ROCE_2X100),
    2453: get(IntfChip.FE, IntfPort.RoCE, IntfGraph.IP40100_V2, IntfI8nKey.ROCE_2X100),
    2454: get(IntfChip.FE, IntfPort.RoCE, IntfGraph.IP40100_V2, IntfI8nKey.FE_NOF_2X100),
    2455: get(IntfChip.BE, IntfPort.ETH, IntfGraph.IP40100_V2, IntfI8nKey.RDMA_BE_2X100),
    2456: get(IntfChip.BE, IntfPort.ETH, IntfGraph.IP40100_V2, IntfI8nKey.RDMA_BE_2X100),
    2457: get(IntfChip.SO, IntfPort.ETH, IntfGraph.IP40100_V2, IntfI8nKey.RDMA_SO_2X100),
    2458: get(IntfChip.SO, IntfPort.ETH, IntfGraph.IP40100_V2, IntfI8nKey.RDMA_SO_2X100),

    2459: get(IntfChip.FE, IntfPort.RoCE, IntfGraph.IP40100_V2, IntfI8nKey.ROCE_2X100),
    2460: get(IntfChip.FE, IntfPort.RoCE, IntfGraph.IP40100_V2, IntfI8nKey.ROCE_2X100),
    2461: get(IntfChip.FE, IntfPort.RoCE, IntfGraph.IP40100_V2, IntfI8nKey.ROCE_2X200),
    2462: get(IntfChip.FE, IntfPort.RoCE, IntfGraph.IP40100_V2, IntfI8nKey.ROCE_2X200),

    4133: get(IntfChip.MGMT, IntfPort.MGMT, IntfGraph.SMM, IntfI8nKey.MGMT),
    4134: get(IntfChip.MGMT, IntfPort.MGMT, IntfGraph.SMM, IntfI8nKey.MGMT),

    4159: get(IntfChip.MGMT, IntfPort.MGMT, IntfGraph.SMM_SQUARE, IntfI8nKey.MGMT),
};


/**
 * 可以更改为前端容器卡的型号
 */
export const avaliableContainerFrontendModuleModels: number[] = [
    2336, 2337, 2331, 2344, 2345, 2378, 2379, 2397, 2398, 2415,
];

/**
 * 安全卡，可以直接使用防勒索特性
 */
export const securityModules: number[] = [2423, 2424, 2425, 2426];

/**
 * 可以更改为后端容器卡的型号
 */
export const avaliableContainerBackendModuleModels: number[] = [
    2340, 2347, 2393, 2401, ...securityModules,
];

/**
 * 可以更改为前端虚拟机卡的型号
 */
export const avaliableVmFrontendModuleModels: number[] = [
    2441, 2452,
];

/**
 * 可以更改为后端虚拟机卡的型号
 */
export const avaliableVmBackendModuleModels: number[] = [
    2441, 2452,
];
